import React, { useEffect, useMemo, useState } from 'react';
import { fetchOutlets, sendTruckAlert, submitDeliveryReport } from './api';
import './App.css';

const stateMapping = {
  MY_KL: 'KL & Selangor',
  MY_NSN: 'Negeri Sembilan',
  MY_MLK: 'Melaka',
  MY_JHR: 'Johor',
  TH_BKK: 'Bangkok',
};

const driverNames = [
  'Halim', 'Suhaimi', 'Raiszuan', 'Hanafi', 'Faiz', 'Asrul', 'Iqbal', 'Azhar',
  'Ariff', 'Nazri', 'Suhud', 'Izzuddin', 'Hasbi', 'Shahrul', 'Imran', 'Fikri',
  'Mahen', 'Chandragan', 'Durgen', 'Other',
];

const lorryPlates = [
  'VMD 8115', 'VMB 8115', 'VGU 8115', 'VGT 8115', 'VGY 8115', 'VJW 8115',
  'VKC 8115', 'VKB 8115', 'VHT 8115', 'VMH 8115', 'VJA 3981', 'BPS 7264',
  'VNM 7121', 'VLJ 4526', 'Other',
];

function malaysiaDate() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kuala_Lumpur',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

const initialForm = {
  outletCode: '',
  driverName: '',
  name: '',
  lorryPlate: '',
  numberPlate: '',
  deliveryDate: malaysiaDate(),
  issues: '',
};

function processOutlets(data) {
  const valid = data.filter((outlet) => outlet.pickup_address && outlet.code);
  return Array.from(new Map(valid.map((outlet) => [outlet.code, outlet])).values())
    .map((outlet) => ({
      ...outlet,
      name: outlet.pickup_address.name.replace(/^Pop Meals @ /, ''),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function outletCoordinates(outlet) {
  const candidates = [outlet?.pickup_address, outlet?.address, outlet?.location, outlet];
  for (const candidate of candidates) {
    const latitude = Number(candidate?.latitude ?? candidate?.lat);
    const longitude = Number(candidate?.longitude ?? candidate?.lng ?? candidate?.lon);
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) return { latitude, longitude };
  }
  return null;
}

function distanceInKilometres(from, to) {
  const radians = (degrees) => degrees * (Math.PI / 180);
  const latitudeDelta = radians(to.latitude - from.latitude);
  const longitudeDelta = radians(to.longitude - from.longitude);
  const a = Math.sin(latitudeDelta / 2) ** 2
    + Math.cos(radians(from.latitude)) * Math.cos(radians(to.latitude))
    * Math.sin(longitudeDelta / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function nearestOutlet(outlets, location) {
  return outlets.reduce((nearest, outlet) => {
    const coordinates = outletCoordinates(outlet);
    if (!coordinates) return nearest;
    const distance = distanceInKilometres(location, coordinates);
    return !nearest || distance < nearest.distance ? { outlet, distance } : nearest;
  }, null);
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Unable to read image'));
    reader.readAsDataURL(file);
  });
}

async function compressImage(file) {
  if (!file.type.startsWith('image/')) throw new Error(`${file.name} is not an image`);
  const source = await fileToDataUrl(file);
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Unable to process ${file.name}`));
    img.src = source;
  });

  const maxDimension = 1600;
  const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.width * scale));
  canvas.height = Math.max(1, Math.round(image.height * scale));
  canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);

  let quality = 0.76;
  let dataUrl = canvas.toDataURL('image/jpeg', quality);
  while (dataUrl.length > 780_000 && quality > 0.36) {
    quality -= 0.08;
    dataUrl = canvas.toDataURL('image/jpeg', quality);
  }
  if (dataUrl.length > 850_000) throw new Error(`${file.name} is too large. Please retake the photo.`);

  return {
    name: file.name.replace(/\.[^.]+$/, '') + '.jpg',
    type: 'image/jpeg',
    data: dataUrl.split(',')[1],
  };
}

function RequiredMark() {
  return <span className="required" aria-label="required">*</span>;
}

function FileField({ id, label, description, required, multiple, files, onChange }) {
  return (
    <div className="field-group">
      <label htmlFor={id}>{label} {required && <RequiredMark />}</label>
      {description && <p className="field-help">{description}</p>}
      <label className="file-picker" htmlFor={id}>
        <span className="camera-icon">＋</span>
        <span>{files.length ? `${files.length} photo${files.length > 1 ? 's' : ''} selected` : 'Take or choose photo'}</span>
      </label>
      <input
        className="visually-hidden"
        id={id}
        type="file"
        accept="image/*"
        capture="environment"
        required={required}
        multiple={multiple}
        onChange={(event) => onChange(Array.from(event.target.files || []))}
      />
      {files.length > 0 && (
        <ul className="file-list">
          {files.map((file, index) => <li key={`${file.name}-${index}`}>{file.name}</li>)}
        </ul>
      )}
    </div>
  );
}

function DeliveryReport({ outlets, loadingOutlets }) {
  const [form, setForm] = useState(initialForm);
  const [location, setLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState('');
  const [arrivalPhotos, setArrivalPhotos] = useState([]);
  const [productPhotos, setProductPhotos] = useState([]);
  const [temperaturePhotos, setTemperaturePhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [outletLocationPromptHandled, setOutletLocationPromptHandled] = useState(false);

  const selectedOutlet = outlets.find((outlet) => outlet.code === form.outletCode);

  const setValue = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const captureLocation = (selectNearest = false) => {
    if (!navigator.geolocation) {
      setLocationStatus('Location is not supported on this device. Select the outlet manually.');
      return;
    }
    setLocationStatus('Getting your current location…');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const currentLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: Math.round(position.coords.accuracy),
        };
        setLocation(currentLocation);
        if (selectNearest) {
          const nearest = nearestOutlet(outlets, currentLocation);
          if (nearest) {
            setValue('outletCode', nearest.outlet.code);
            setLocationStatus(`Nearest outlet selected: ${nearest.outlet.name} · ${nearest.distance.toFixed(1)} km away`);
            return;
          }
          setLocationStatus('Location captured, but outlet coordinates were unavailable. Select the outlet manually.');
          return;
        }
        setLocationStatus('Location captured');
      },
      (error) => {
        const message = error.code === 1
          ? 'Location permission was denied. Allow location access and try again.'
          : 'Could not get your location. Move to an open area and try again.';
        setLocationStatus(message);
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  };

  const handleOutletPointerDown = (event) => {
    if (loadingOutlets || outletLocationPromptHandled) return;
    event.preventDefault();
    setOutletLocationPromptHandled(true);
    captureLocation(true);
  };

  const resetForm = () => {
    setForm({ ...initialForm, deliveryDate: malaysiaDate() });
    setLocation(null);
    setLocationStatus('');
    setArrivalPhotos([]);
    setProductPhotos([]);
    setTemperaturePhotos([]);
    setResult(null);
    setOutletLocationPromptHandled(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setResult(null);
    if (!location) {
      setResult({ type: 'error', message: 'Capture your current location before submitting.' });
      return;
    }
    if (arrivalPhotos.length !== 1 || temperaturePhotos.length !== 1) {
      setResult({ type: 'error', message: 'Arrival and lorry temperature photos are required.' });
      return;
    }
    if (productPhotos.length > 3) {
      setResult({ type: 'error', message: 'You can attach up to 3 product photos.' });
      return;
    }

    setSubmitting(true);
    try {
      const [arrivalPhoto, productPhotoData, temperaturePhoto] = await Promise.all([
        Promise.all(arrivalPhotos.map(compressImage)),
        Promise.all(productPhotos.map(compressImage)),
        Promise.all(temperaturePhotos.map(compressImage)),
      ]);
      const response = await submitDeliveryReport({
        submission: {
          ...form,
          outletName: selectedOutlet?.name || '',
          latitude: location.latitude,
          longitude: location.longitude,
          locationAccuracy: location.accuracy,
          userAgent: navigator.userAgent,
        },
        files: {
          arrivalPhoto,
          productPhotos: productPhotoData,
          temperaturePhoto,
        },
      });
      setResult({ type: 'success', message: `Report submitted successfully. Reference: ${response.submissionId.slice(0, 8)}` });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setResult({ type: 'error', message: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="form-card">
      <div className="form-heading">
        <span className="eyebrow">LOGISTICS</span>
        <h1>Driver Outbound Delivery Form</h1>
        <p>Complete this report at the receiving or loading area.</p>
      </div>

      {result && (
        <div className={`notice ${result.type}`} role="alert">
          <strong>{result.type === 'success' ? 'Submitted' : 'Please check'}</strong>
          <span>{result.message}</span>
          {result.type === 'success' && <button type="button" className="text-button" onClick={resetForm}>Submit another report</button>}
        </div>
      )}

      {result?.type !== 'success' && (
        <form onSubmit={handleSubmit}>
          <div className="field-group">
            <label htmlFor="outlet">Outlet <RequiredMark /></label>
            <p className="field-help">Tap to allow location access and automatically select the nearest active outlet.</p>
            <select id="outlet" value={form.outletCode} onPointerDown={handleOutletPointerDown} onChange={(event) => setValue('outletCode', event.target.value)} required disabled={loadingOutlets}>
              <option value="">{loadingOutlets ? 'Loading active outlets…' : 'Select outlet'}</option>
              {outlets.map((outlet) => <option key={outlet.code} value={outlet.code}>{outlet.name}</option>)}
            </select>
          </div>

          <div className="field-group">
            <label>Your Location <RequiredMark /></label>
            <p className="field-help">Capture your GPS location while you are at the outlet.</p>
            <button type="button" className="location-button" onClick={() => captureLocation(false)}>
              <span>⌖</span>{location ? 'Refresh location' : 'Capture current location'}
            </button>
            {locationStatus && <p className={`location-status ${location ? 'captured' : ''}`}>{locationStatus}{location ? ` · ±${location.accuracy} m` : ''}</p>}
          </div>

          <div className="two-column">
            <div className="field-group">
              <label htmlFor="driverName">Driver Lorry Name <RequiredMark /></label>
              <select id="driverName" value={form.driverName} onChange={(event) => setValue('driverName', event.target.value)} required>
                <option value="">Select driver</option>
                {driverNames.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </div>
            <div className="field-group">
              <label htmlFor="name">Name <RequiredMark /></label>
              <input id="name" type="text" value={form.name} onChange={(event) => setValue('name', event.target.value)} autoComplete="name" maxLength="100" required />
            </div>
          </div>

          <div className="two-column">
            <div className="field-group">
              <label htmlFor="lorryPlate">Lorry Number Plate <RequiredMark /></label>
              <select id="lorryPlate" value={form.lorryPlate} onChange={(event) => {
                setValue('lorryPlate', event.target.value);
                if (event.target.value !== 'Other') setValue('numberPlate', event.target.value);
              }} required>
                <option value="">Select lorry</option>
                {lorryPlates.map((plate) => <option key={plate} value={plate}>{plate}</option>)}
              </select>
            </div>
            <div className="field-group">
              <label htmlFor="numberPlate">Number Plate <RequiredMark /></label>
              <input id="numberPlate" type="text" value={form.numberPlate} onChange={(event) => setValue('numberPlate', event.target.value.toUpperCase())} placeholder="Enter number plate" maxLength="20" required />
            </div>
          </div>

          <div className="field-group">
            <label htmlFor="deliveryDate">Delivery Date <RequiredMark /></label>
            <p className="field-help">Automatically set to today.</p>
            <input id="deliveryDate" type="date" value={form.deliveryDate} disabled aria-disabled="true" />
          </div>

          <FileField id="arrivalPhoto" label="Time arrived receiving/loading area" description="Ambil gambar jam berlatarkan kawasan loading bay." required files={arrivalPhotos} onChange={(files) => setArrivalPhotos(files.slice(0, 1))} />
          <FileField id="productPhotos" label="Gambar Product" description="Optional · up to 3 photos" multiple files={productPhotos} onChange={(files) => setProductPhotos(files.slice(0, 3))} />

          <div className="field-group">
            <label htmlFor="issues">Any issues happens? <RequiredMark /></label>
            <p className="field-help">Contoh: staff datang lambat, loading bay penuh dan sebagainya.</p>
            <textarea id="issues" rows="4" value={form.issues} onChange={(event) => setValue('issues', event.target.value)} placeholder="Write ‘None’ if there were no issues" maxLength="1000" required />
          </div>

          <FileField id="temperaturePhoto" label="Gambar Lorry temperature sebelum bukak pintu" required files={temperaturePhotos} onChange={(files) => setTemperaturePhotos(files.slice(0, 1))} />

          <button className="submit-button" type="submit" disabled={submitting || loadingOutlets}>
            {submitting ? 'Compressing photos and submitting…' : 'Submit delivery report'}
          </button>
          <p className="privacy-note">Photos and report details are stored securely in Pop Meals Google Workspace.</p>
        </form>
      )}
    </main>
  );
}

function TruckAlerts({ outlets, loadingOutlets }) {
  const [selectedState, setSelectedState] = useState('');
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [status, setStatus] = useState('');

  const states = useMemo(() => Array.from(new Set(outlets.map((outlet) => outlet.code.split('_').slice(0, 2).join('_')))), [outlets]);
  const filteredOutlets = outlets.filter((outlet) => outlet.code.startsWith(selectedState));
  const stateName = (code) => stateMapping[code] || code;

  const handleTruckAction = async (truckStatus) => {
    if (!selectedOutlet) return;
    setStatus('Sending alert…');
    try {
      await sendTruckAlert(selectedOutlet.code, truckStatus);
      setStatus(`Truck status for ${selectedOutlet.name} updated successfully.`);
    } catch (error) {
      setStatus(error.message);
    }
  };

  return (
    <main className="form-card alert-card">
      <div className="form-heading">
        <span className="eyebrow">LOGISTICS</span>
        <h1>Truck Arrival Alert</h1>
        <p>Notify an outlet when the truck is approaching or has arrived.</p>
      </div>
      {!selectedOutlet ? (
        <>
          <div className="field-group">
            <label htmlFor="state">State</label>
            <select id="state" value={selectedState} onChange={(event) => setSelectedState(event.target.value)} disabled={loadingOutlets}>
              <option value="">{loadingOutlets ? 'Loading active outlets…' : 'Select state'}</option>
              {states.map((state) => <option key={state} value={state}>{stateName(state)}</option>)}
            </select>
          </div>
          <div className="outlet-grid">
            {filteredOutlets.map((outlet) => <button key={outlet.code} type="button" className="outlet-button" onClick={() => setSelectedOutlet(outlet)}>{outlet.name}</button>)}
          </div>
        </>
      ) : (
        <div className="alert-actions">
          <h2>{selectedOutlet.name}</h2>
          <button type="button" className="submit-button" onClick={() => handleTruckAction('ARRIVING_IN_10_MINUTES')}>Truck arriving in 10 minutes</button>
          <button type="button" className="submit-button secondary" onClick={() => handleTruckAction('ARRIVED')}>Truck has arrived</button>
          <button type="button" className="text-button" onClick={() => { setSelectedOutlet(null); setStatus(''); }}>Back to outlets</button>
          {status && <p className="location-status">{status}</p>}
        </div>
      )}
    </main>
  );
}

function App() {
  const [view, setView] = useState('report');
  const [outlets, setOutlets] = useState([]);
  const [loadingOutlets, setLoadingOutlets] = useState(true);
  const [outletError, setOutletError] = useState('');

  useEffect(() => {
    fetchOutlets()
      .then((data) => setOutlets(processOutlets(data)))
      .catch(() => setOutletError('Active outlets could not be loaded. Refresh the page to try again.'))
      .finally(() => setLoadingOutlets(false));
  }, []);

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand-mark">PM</div>
        <div className="brand-copy"><strong>Pop Meals</strong><span>Driver Portal</span></div>
        <nav aria-label="Portal sections">
          <button className={view === 'report' ? 'active' : ''} onClick={() => setView('report')}>Delivery Report</button>
          <button className={view === 'alerts' ? 'active' : ''} onClick={() => setView('alerts')}>Truck Alert</button>
        </nav>
      </header>
      {outletError && <div className="global-error">{outletError}</div>}
      {view === 'report'
        ? <DeliveryReport outlets={outlets} loadingOutlets={loadingOutlets} />
        : <TruckAlerts outlets={outlets} loadingOutlets={loadingOutlets} />}
      <footer>Pop Meals Logistics · Internal use only</footer>
    </div>
  );
}

export default App;
